import React from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Typography,
} from '@mui/material';
import { AccountBalance as AccountBalanceIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import PrimaryActionButton from '../common/PrimaryActionButton';
import { BRAND, brandAlpha } from '../../theme/brandTokens';

const actionButtonSx = { px: 3, py: 1.25, whiteSpace: 'nowrap' };

const secondaryActionButtonSx = {
  background: BRAND.surface,
  boxShadow: 'none',
  border: `1px solid ${BRAND.accent}`,
  color: BRAND.accentText,
  '&:hover': {
    background: brandAlpha('accent', 0.08),
    borderColor: BRAND.accentDark,
    color: BRAND.accentDark,
    boxShadow: 'none',
  },
};

const CARD_PAYMENTS_STATUS = {
  Active: 'active',
  Pending: 'pending',
  Inactive: 'inactive',
};

const StoreStripeConnectOnboardingCard = ({
  onboardingStatus,
  loading,
  actionLoading,
  isComplete,
  isMembershipBillingReady = true,
  onStartOnboarding,
  onRefreshStatus,
  compact = false,
}) => {
  const { t } = useTranslation();

  if (loading && !onboardingStatus) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: compact ? 1 : 2 }}>
        <CircularProgress size={20} sx={{ color: BRAND.accent }} />
        <Typography variant="body2" color="text.secondary">
          {t('stores.stripeConnect.loading')}
        </Typography>
      </Box>
    );
  }

  if (!onboardingStatus || (isComplete && isMembershipBillingReady)) {
    return null;
  }

  const isNotStarted = onboardingStatus.onboardingStatus === 'NotStarted';
  const isMembershipBillingPending = isComplete && !isMembershipBillingReady;
  const cardPaymentsStatus = onboardingStatus.cardPaymentsStatus
    ?? (isMembershipBillingPending && onboardingStatus.chargesEnabled
      ? CARD_PAYMENTS_STATUS.Pending
      : null);

  let titleKey;
  let descriptionKey;
  let actionKey;

  if (isMembershipBillingPending) {
    titleKey = cardPaymentsStatus === CARD_PAYMENTS_STATUS.Inactive
      ? 'stores.stripeConnect.membershipBilling.inactive.title'
      : 'stores.stripeConnect.membershipBilling.pending.title';
    descriptionKey = cardPaymentsStatus === CARD_PAYMENTS_STATUS.Inactive
      ? 'stores.stripeConnect.membershipBilling.inactive.description'
      : 'stores.stripeConnect.membershipBilling.pending.description';
    actionKey = cardPaymentsStatus === CARD_PAYMENTS_STATUS.Pending
      ? 'stores.stripeConnect.actions.refresh'
      : 'stores.stripeConnect.actions.continue';
  } else if (isNotStarted) {
    titleKey = 'stores.stripeConnect.notStarted.title';
    descriptionKey = 'stores.stripeConnect.notStarted.description';
    actionKey = 'stores.stripeConnect.actions.start';
  } else {
    titleKey = 'stores.stripeConnect.pending.title';
    descriptionKey = 'stores.stripeConnect.pending.description';
    actionKey = 'stores.stripeConnect.actions.continue';
  }

  const content = (
    <>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 240 }}>
          {!compact ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <AccountBalanceIcon sx={{ color: BRAND.accentText }} />
              <Typography variant="h6" sx={{ color: BRAND.accentText, fontWeight: 600 }}>
                {t('stores.stripeConnect.title')}
              </Typography>
            </Box>
          ) : null}
          <Typography variant={compact ? 'subtitle1' : 'body1'} sx={{ fontWeight: 600, mb: 0.75 }}>
            {t(titleKey)}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t(descriptionKey)}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {!isMembershipBillingPending ? (
              <Chip
                size="small"
                label={t(`stores.stripeConnect.status.${onboardingStatus.onboardingStatus}`)}
                sx={{ backgroundColor: '#fff3e0', color: '#ef6c00' }}
              />
            ) : null}
            {onboardingStatus.detailsSubmitted ? (
              <Chip
                size="small"
                label={t('stores.stripeConnect.flags.detailsSubmitted')}
                sx={{ backgroundColor: '#e3f2fd', color: '#1565c0' }}
              />
            ) : null}
            {isMembershipBillingPending && cardPaymentsStatus ? (
              <Chip
                size="small"
                label={t(`stores.stripeConnect.flags.cardPayments.${cardPaymentsStatus}`)}
                sx={{ backgroundColor: '#fff3e0', color: '#ef6c00' }}
              />
            ) : null}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'stretch', minWidth: compact ? 180 : 220 }}>
          {actionKey !== 'stores.stripeConnect.actions.refresh' ? (
            <PrimaryActionButton
              onClick={onStartOnboarding}
              disabled={actionLoading}
              sx={actionButtonSx}
            >
              {actionLoading ? t('stores.stripeConnect.actions.opening') : t(actionKey)}
            </PrimaryActionButton>
          ) : null}
          <PrimaryActionButton
            variant={actionKey === 'stores.stripeConnect.actions.refresh' ? 'contained' : 'outlined'}
            onClick={onRefreshStatus}
            disabled={actionLoading}
            sx={actionKey === 'stores.stripeConnect.actions.refresh'
              ? actionButtonSx
              : [actionButtonSx, secondaryActionButtonSx]}
          >
            {actionLoading ? t('stores.stripeConnect.actions.opening') : t('stores.stripeConnect.actions.refresh')}
          </PrimaryActionButton>
        </Box>
      </Box>
    </>
  );

  if (compact) {
    return (
      <Alert severity="warning" sx={{ mb: 3 }}>
        {content}
      </Alert>
    );
  }

  return (
    <Box sx={{
      p: 2,
      backgroundColor: BRAND.background,
      borderRadius: 1,
      border: `1px solid ${BRAND.line}`,
      mb: 2,
    }}
    >
      {content}
    </Box>
  );
};

export default StoreStripeConnectOnboardingCard;
